import { Box, Checkbox, IconButton, Typography } from "@mui/material"
import { useDraggable } from "@dnd-kit/core"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { ListItemBox } from "lib/@components/ListItemBox"
import { TruncatedTypography } from "lib/@components/truncated-typography"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import Iconify from "minimals-template/components/Iconify"
import { groupSelectionState } from "./board-selection"
import { UnassignedScheduleRow } from "./unassigned-schedule-row"
import { formatHours } from "./format-hours"

/**
 * One library folder on the left pane. The header is draggable and its checkbox selects
 * the folder itself (see board-selection.js). Folders start collapsed, except while
 * searching, so large regimes stay scannable. The board owns which are open, so a
 * shift-click range only covers schedules the user can see.
 */
export function UnassignedFolderGroup({ group }) {
    const { selection, readOnly, onToggleGroup, isGroupOpen, onToggleGroupOpen } = useBoundContext()
    const open = isGroupOpen(group)
    const state = groupSelectionState(selection, group)
    const hours = group.schedules.reduce(addHours, 0)
    const toggleLabel = open ? "Collapse" : "Expand"
    const toggleIcon = open ? "eva:chevron-up-fill" : "eva:chevron-down-fill"
    const cursor = readOnly ? "default" : "grab"
    // Pointer listeners only, as on the schedule rows
    const { listeners, setNodeRef, isDragging } = useDraggable({
        id: `group:${group.key}`,
        data: { group },
        disabled: readOnly,
    })

    return (
        <Box data-cy="unassigned-folder-group">
            <ListItemBox
                ref={setNodeRef}
                spacing={1}
                {...listeners}
                data-cy="assignment-folder-header"
                // px matches the pane header so every tick box (select all, folders,
                // schedules) sits in one column
                sx={{
                    pl: 2,
                    pr: 1,
                    py: 0.5,
                    bgcolor: "background.neutral",
                    borderTop: 1,
                    borderColor: "divider",
                    cursor,
                    opacity: dragOpacity(isDragging),
                }}
            >
                <If value={!readOnly}>
                    <Checkbox
                        size="small"
                        checked={state === "all"}
                        indeterminate={state === "some"}
                        onChange={handleToggle}
                        onClick={stopPropagation}
                        inputProps={{ "aria-label": `Select folder ${group.label}` }}
                        data-cy="assignment-folder-checkbox"
                    />
                </If>
                <Iconify icon="mdi:folder-outline" width={18} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <TruncatedTypography variant="body2" fontWeight={500}>
                        {group.label}
                    </TruncatedTypography>
                </Box>
                <Typography variant="caption" color="text.secondary" noWrap>
                    {group.schedules.length} {"schedule".pluralize(group.schedules.length)}, {formatHours(hours)}
                </Typography>
                <IconButton
                    size="small"
                    onClick={toggleExpanded}
                    aria-label={toggleLabel}
                    data-cy="assignment-folder-toggle"
                >
                    <Iconify icon={toggleIcon} />
                </IconButton>
            </ListItemBox>
            <If value={open}>
                <Repeat
                    list={group.schedules}
                    keyFn={scheduleKey}
                    pass="schedule"
                    item={<UnassignedScheduleRow group={group} />}
                />
            </If>
        </Box>
    )

    function toggleExpanded(event) {
        event.stopPropagation()
        onToggleGroupOpen(group)
    }

    function handleToggle() {
        onToggleGroup(group)
    }
}

function stopPropagation(event) {
    event.stopPropagation()
}

function addHours(total, schedule) {
    return total + (schedule.annualHours || 0)
}

function scheduleKey(schedule) {
    return schedule.scheduleId
}

function dragOpacity(isDragging) {
    return isDragging ? 0.5 : 1
}
